<template>
	<div class="overlay-dialog">
		<h2
			class="overlay-dialog__title"
			v-html="$t('account.addressEdit.title')"
		/>
		<GeneralMessage
			ref="message"
			v-if="response.message"
			:message="response.message"
			:type="response.type"
			@close="response.message = ''"
		/>
		<ValidationObserver
			ref="form"
			v-slot="{ invalid, errors }"
			tag="form"
			@submit.prevent="submit"
			novalidate
		>
			<ValidationObserver
				tag="section"
				class="account__address-form"
				ref="userAddress"
				vid="userAddress"
				v-slot="{ invalid }"
			>
				<FormInputSelect
					:options="genders"
					v-model="form.userAddress.gender"
					name="gender"
					:label="$t('forms.labels.gender')"
					:labelOption="$t('forms.labels.optional')"
					@input="onInput"
					:disabled="loading"
				/>
				<FormInputDate
					:placeholder="$t('forms.labels.birthday')"
					:label="$t('forms.labels.birthday')"
					:labelOption="$t('forms.labels.optional')"
					v-model="form.userAddress.birthday"
					max-date="today"
					:disabled="!!accountUserDetail.birthday"
				/>

				<ValidationProvider
					name="shippingFirstname"
					rules="required"
					v-slot="{ errors, invalid, validated }"
					tag="div"
					:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
				>
					<FormInput
						type="text"
						:placeholder="$t('forms.labels.firstname')"
						:label="$t('forms.labels.firstname')"
						name="firstname"
						v-model="form.userAddress.firstName"
						:error="invalid && validated"
						:error-message="errors[0]"
						required
						@input="onInput"
						:disabled="loading"
					/>
				</ValidationProvider>
				<ValidationProvider
					name="shippingLastname"
					rules="required"
					v-slot="{ errors, invalid, validated }"
					tag="div"
					:customMessages="{ required: $t('forms.errors.fieldEmpty') }"
				>
					<FormInput
						type="text"
						:placeholder="$t('forms.labels.lastname')"
						:label="$t('forms.labels.lastname')"
						name="lastname"
						v-model="form.userAddress.lastName"
						:error="invalid && validated"
						:error-message="errors[0]"
						required
						@input="onInput"
						:disabled="loading"
					/>
				</ValidationProvider>
				<FormInput
					type="text"
					:placeholder="$t('forms.labels.company')"
					:label="$t('forms.labels.company')"
					:labelOption="$t('forms.labels.optional')"
					name="company"
					v-model="form.userAddress.company"
					@input="onInput"
					:disabled="loading"
				/>

				<div class="form-group">
					<div
						v-if="!themeValue('THEME_REVERSE_STREET_NUMBER')"
						class="form-group__span--9"
					>
						<FormInput
							type="text"
							:placeholder="$t('forms.labels.street')"
							:label="$t('forms.labels.street')"
							:labelOption="$t('forms.labels.optional')"
							name="street"
							v-model="form.userAddress.street"
							@input="onInput"
							:disabled="loading"
						/>
					</div>
					<div class="form-group__span--3">
						<FormInput
							type="text"
							:placeholder="$t('forms.labels.number')"
							:label="$t('forms.labels.number')"
							:labelOption="$t('forms.labels.optional')"
							name="number"
							v-model="form.userAddress.streetNumber"
							@input="onInput"
							:disabled="loading"
						/>
					</div>
					<div
						v-if="themeValue('THEME_REVERSE_STREET_NUMBER')"
						class="form-group__span--9"
					>
						<FormInput
							type="text"
							:placeholder="$t('forms.labels.street')"
							:label="$t('forms.labels.street')"
							:labelOption="$t('forms.labels.optional')"
							name="street"
							v-model="form.userAddress.street"
							@input="onInput"
							:disabled="loading"
						/>
					</div>
				</div>

				<ValidationObserver
					class="input-row__wrapper"
					tag="div"
					ref="zipCity"
					v-slot="{ dirty, validated, invalid, errors }"
				>
					<div class="form-group">
						<ValidationProvider
							class="form-group__span--4 form-group__span--xxl-3"
							name="shippingZip"
							rules="zip"
							v-slot="{ errors, invalid }"
							tag="div"
							:customMessages="{ zip: $t('forms.errors.zip') }"
						>
							<FormInputZip
								v-model="form.userAddress.postcode"
								:placeholder="$t('forms.labels.zip')"
								:label="$t('forms.labels.zip')"
								:labelOption="$t('forms.labels.optional')"
								name="zip"
								:error="invalid && validated"
								:disabled="loading"
								@input="onInput"
							/>
						</ValidationProvider>
						<div class="form-group__span--8 form-group__span--xxl-9">
							<FormInput
								type="text"
								:placeholder="$t('forms.labels.city')"
								:label="$t('forms.labels.city')"
								:labelOption="$t('forms.labels.optional')"
								name="city"
								v-model="form.userAddress.city"
								@input="onInput"
								:disabled="loading"
							/>
						</div>
					</div>
					<div
						class="form-input__error | form-input-error form-group__span--12"
						v-if="invalid && (validated || dirty)"
						v-html="Object.values(errors).filter(value => !!value.join('').trim()).join(' ')"
					/>
				</ValidationObserver>

				<FormInputSelect
					:options="countryOptions"
					v-model="form.userAddress.countryCode"
					:label="$t('forms.labels.country')"
					name="country"
					@input="onInput"
					:disabled="loading"
				/>
				<ValidationProvider
					name="shippingPhone"
					rules="required|phone_int"
					v-slot="{ errors, invalid, validated }"
					tag="div"
					:customMessages="{ required: $t('forms.errors.fieldEmpty'), phone_int: $t('forms.errors.phone') }"
				>
					<FormInput
						type="tel"
						:placeholder="$t('forms.labels.phone')"
						:label="$t('forms.labels.phone')"
						name="phone"
						v-model="form.userAddress.phoneNumber"
						:error="invalid && validated"
						:error-message="errors[0]"
						required
						@input="onInput"
						:disabled="loading"
						:hint="$t('login.phoneHint')"
					/>
				</ValidationProvider>
			</ValidationObserver>

			<Button
				type="submit"
				level="primary"
				:text="$t('account.addressEdit.save')"
				:disabled="loading"
			/>
		</ValidationObserver>
	</div>
</template>

<script>
import Vue from "vue"
import { mapGetters } from "vuex"
import { ValidationProvider, ValidationObserver } from "vee-validate"
import FormInput from "@/components/FormInput"
import FormInputZip from "@/components/FormInputZip"
import FormInputTextarea from "@/components/FormInputTextarea"
import FormInputCheckbox from "@/components/FormInputCheckbox"
import FormInputSelect from "@/components/FormInputSelectCustom"
import FormInputDate from "@/components/FormInputDate"
import GeneralMessage from '@/components/GeneralMessage'
import Button from '@/components/Button'
import scrrroll from 'scrrroll'
import { switchDateFormat } from "@/utils/formatDate"

export default {
	components: {
		ValidationProvider,
		ValidationObserver,
		FormInput,
		FormInputZip,
		FormInputTextarea,
		FormInputCheckbox,
		FormInputSelect,
		FormInputDate,
		GeneralMessage,
		Button,
	},
	data() {
		return {
			countryOptions: (this.themeValue('THEME_COUNTRY_LIST') || []).map(country => ({
				value: country,
				label: this.$t(`forms.countries.${country}`)
			})),
			genders: [
				{
					value: '',
					label: this.$t('forms.genders.none'),
					reset: true,
				},
				{
					value: 'f',
					label: this.$t('forms.genders.f'),
				},
				{
					value: 'm',
					label: this.$t('forms.genders.m'),
				},
				{
					value: 'd',
					label: this.$t('forms.genders.d'),
				},
			],
			form: {
				userAddress: {
					gender: "",
					firstName: "",
					lastName: "",
					countryCode: this.themeValue('THEME_COUNTRY'),
					street: "",
					streetNumber: "",
					city: "",
					postcode: "",
					provinceName: "",
					company: "",
					phoneNumber: "",
					birthday: "",
				},
			},
			response: {
				message: '',
				type: '',
			},
			edited: false,
		};
	},
	computed: {
		...mapGetters("account", [
			"isLoggedIn",
			"accountLastAddress",
			"accountUserDetail",
		]),
		...mapGetters('loader', [
			'isLoading',
		]),
		loading: {
			set(value) {
				this.$store.dispatch('loader/setLoading', value);
				this.$emit('loading', value);
			},
			get() {
				return this.isLoading;
			}
		},
	},
	methods: {
		onInput() {
			this.edited = true;
		},
		async submit() {
			this.loading = true
			const isValid = await this.$refs.form.validate()
			if (isValid) {
				this.response.message = '';
				try {
					await Promise.all([
						this.$store.dispatch('account/updateUser', { userDetail: { ...this.accountUserDetail, ...this.form.userAddress, birthday: switchDateFormat(this.form.userAddress.birthday, '-') }, accumulatorGroup: 'userupdate'}),
						this.$store.dispatch('account/updateAddress', {...this.form, accumulatorGroup: 'userupdate'}),
					])
					this.edited = false;
					this.response.message = this.$t('account.addressEdit.success');
					this.response.type = 'success';
				}
				catch(err) {
					let notificationMessage = err.response.data;
					if (typeof notificationMessage === "object") {
						let errors = notificationMessage.errors
						if (errors) {
							//handle errors
							notificationMessage = Object.values(errors)
								.map(v => v[0])
								.join(", ")
						} else {
							notificationMessage = notificationMessage.message
						}
					}
					this.response.message = notificationMessage || this.$t('forms.errors.generic');
					this.response.type = 'error';

				}
				this.$parent.scrollTop();
			}
			this.loading = false
		},
		async onClose() {
			if (this.edited) {
				const confirmed = await this.$store.dispatch('dialog/showDialog', {
					title: this.$t('account.addressEdit.dialogTitle'),
					message: this.$t('account.addressEdit.dialogMessage'),
					confirm: this.$t('account.addressEdit.dialogConfirm'),
					cancel: this.$t('account.addressEdit.dialogCancel'),
				});
				if (confirmed) {
					return;
				}
			}
			this.$emit('close');
		}
	},
	created() {
		if (this.accountUserDetail) {
			Object.keys(this.form.userAddress).forEach(key => {
				if (this.accountUserDetail[key] !== undefined) {
					Vue.set(this.form.userAddress, key, ''+this.accountUserDetail[key]);
				}
			});
		}
		if (this.accountLastAddress) {
			Object.keys(this.form.userAddress).forEach(key => {
				if (this.accountLastAddress[key] !== undefined) {
					Vue.set(this.form.userAddress, key, ''+this.accountLastAddress[key]);
				}
			});
		}
	}
}
</script>

<style>

</style>
